
import { BLOOD_RELATIONSHIP_CODES } from '../../constants/ssap/hhmTypes/bloodRelationshipCodesList';
import { householdMemberTools } from './householdMemberTools';
import { PRIMARY_CONTACT_PERSON_TYPES } from '../../constants/ssap/hhmTypes/primaryContactPersonTypes';
import { SSAP_FLOW_VERSION } from '../../constants/ssap/miscellaneous/miscellaneous';

const processDataStringifyingUiProperties = data => {
    const parsedData = JSON.parse(data);
    return {
        ...parsedData,
        uiProperties: JSON.stringify(parsedData.uiProperties)
    };
};

const resolveSsap = (data, skipUiPropertiesConversion) => {


    const processUiProperties = uiProperties => {
        if(typeof uiProperties === 'string' && !skipUiPropertiesConversion) {
            return {
                ...JSON.parse(uiProperties),
                ssapFlowVersion: SSAP_FLOW_VERSION
            };
        } else {
            return uiProperties;
        }
    };
    
    return data.singleStreamlinedApplication ?
        {
            ...data.singleStreamlinedApplication,
            uiProperties: processUiProperties(data.singleStreamlinedApplication.uiProperties)
        } :
        {
            ...data,
            uiProperties: processUiProperties(data.uiProperties)
        };
};

const getTaxHouseholdType = data => {
    const resolvedData = resolveSsap(data);
    return Array.isArray(resolvedData.taxHousehold) ? 'array' : typeof resolvedData.taxHousehold;
};

const getBloodRelationshipPlace = data => {
    const resolvedData = resolveSsap(data);
    switch(true) {
        case Array.isArray(resolvedData.taxHousehold.bloodRelationship):
            return 'household';
        default:
            return 'primaryContact';
    }
};

const addGuids = taxHousehold => {
    taxHousehold.householdMember.forEach(hhm => {
        const {
            employerSponsoredCoverage
        } = hhm;
        employerSponsoredCoverage.forEach(c => {
            c.applicantGuid = c.applicantGuid ? c.applicantGuid : householdMemberTools.extractFromHouseholdMembers(taxHousehold.householdMember).getGuidByPersonId(c.personId);
        });
    });
    return taxHousehold;
};

const resolveTaxHousehold = data => {
    const resolvedData = resolveSsap(data);
    return Array.isArray(resolvedData.taxHousehold) ?
        addGuids(resolvedData.taxHousehold[0]) :
        addGuids(resolvedData.taxHousehold);
};


const convertTaxHousehold = (data, type = undefined, skipUiPropertiesConversion = undefined) => {

    const resolvedData = resolveSsap(data, skipUiPropertiesConversion);

    if(!type) {
        return resolvedData;
    }

    switch(type) {
        case 'array':
            return {
                ...resolvedData,
                taxHousehold: [ resolveTaxHousehold(data) ]
            };
        case 'object':
            return {
                ...resolvedData,
                taxHousehold: resolveTaxHousehold(data)
            };
    }
};

export const processBloodRelationships = (bloodRelationships, householdMembers) => {
    return bloodRelationships.map(br => {

        const {
            personGuid,
            relation
        } = br;

        if(relation === BLOOD_RELATIONSHIP_CODES.PARENT_OF_CHILD) {
            const isFosterParent = !!householdMembers.find(hhm => {
                const {
                    applicantGuid,
                    fosterParentIndicator
                } = hhm;

                return fosterParentIndicator === true && applicantGuid === personGuid;
            });

            if(isFosterParent) {
                return {
                    ...br,
                    relation: BLOOD_RELATIONSHIP_CODES.FOSTER_PARENT
                };
            }
        }

        return br;
    });
};

const convertBloodRelationships = (data, place, structure = undefined, shouldProcessBloodRelationships = undefined) => {

    const dataStructure = structure ? structure : getTaxHouseholdType(data);
    
    const convertedData = convertTaxHousehold(data, 'object');

    const primaryContact = householdMemberTools.getPrimaryContact(convertedData.taxHousehold.householdMember);
    
    let bloodRelationship = [];
    
    switch(true) {
        case !!convertedData.taxHousehold.bloodRelationship:
            bloodRelationship = convertedData.taxHousehold.bloodRelationship; break;
        case !!(primaryContact && primaryContact.bloodRelationship):
            bloodRelationship = primaryContact.bloodRelationship; break;
        default:
            // TODO: temporarily for "fire case"
            (hhm => 
                hhm ? 
                    bloodRelationship = hhm.bloodRelationship : 
                    bloodRelationship = []
            )(convertedData
                .taxHousehold
                .householdMember
                .find(hhm => PRIMARY_CONTACT_PERSON_TYPES.some(pt => pt === hhm.applicantPersonType)));
    }

    bloodRelationship = bloodRelationship.map(br => (!br.personGuid || !br.relatedPersonGuid) ?
        {
            ...br,
            personGuid: householdMemberTools.extractFromHouseholdMembers(convertedData.taxHousehold.householdMember).getGuidByPersonId(+br.individualPersonId),
            relatedPersonGuid: householdMemberTools.extractFromHouseholdMembers(convertedData.taxHousehold.householdMember).getGuidByPersonId(+br.relatedPersonId)
        } :
        br
    );

    if(shouldProcessBloodRelationships) {
        bloodRelationship = processBloodRelationships(bloodRelationship, convertedData.taxHousehold.householdMember);
    }
    

    let output = {
        ...convertedData,
        taxHousehold: {
            ...convertedData.taxHousehold,
            bloodRelationship,
            householdMember: convertedData.taxHousehold.householdMember.map(hhm => ({
                ...hhm,
                bloodRelationship: []
            }))
        }
    };
    
    switch(place) {
        case 'household':
            return convertTaxHousehold(output, dataStructure);
        case 'primaryContact':
            return convertTaxHousehold((output => {
                delete output.taxHousehold.bloodRelationship;
                output.taxHousehold.householdMember = householdMemberTools.updatePrimaryContact(output.taxHousehold.householdMember, { bloodRelationship });
                return output;
            })(output), dataStructure);
    }
};

export const processJsonTools = {
    convertBloodRelationships,
    convertTaxHousehold,
    getBloodRelationshipPlace,
    getTaxHouseholdType,
    processDataStringifyingUiProperties,
    resolveTaxHousehold
};